import React from 'react';
import { graphql } from 'react-relay';
import { TopNavMiddle, TopNavStart, TopNavEnd } from '@atlaskit/navigation-system/layout/top-nav';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { isNotFoundError } from '@confluence/error-boundary/entry-points/isNotFoundError.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import type { SessionDataType } from '@confluence/session-data/entry-points/SessionDataTypes.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

import { AppSwitcherButton } from '../AppSwitcherButton.tsx';
import { DeveloperButtons } from '../DeveloperButtons.tsx';
import { LoginButton } from '../LoginButton.tsx';
import { NotificationsButton } from '../NotificationsButton.tsx';
import { RovoButton } from '../RovoButton.tsx';
import { SearchBar } from '../SearchBar.tsx';
//please remove photo reliance on this query so it can be cleaned up
import AppNavigationUnifiedQuery from '../Nav3/__generated__/AppNavigationUnifiedQuery.graphql.ts';
import type {
	AppNavigationUnifiedQuery as AppNavigationUnifiedQueryType,
	AppNavigationUnifiedQuery_user_AtlassianUser as AppNavigationUnifiedQuery_user,
} from '../Nav3/__types__/AppNavigationUnifiedQuery.ts';

import { CompanyHubProfileMenu } from './CompanyHubProfileMenu.tsx';
import type {
	CompanyHubTopNavigationQuery,
	CompanyHubTopNavigationQuery_user_AtlassianUser as CompanyHubTopNavigationQuery_user,
} from './__types__/CompanyHubTopNavigationQuery.ts';
import { CompanyHubSiteLogoButton } from './CompanyHubSiteLogoButton.tsx';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
export type User = {
	id: CompanyHubTopNavigationQuery_user['id'];
	displayName: SessionDataType['displayName'];
	photos: AppNavigationUnifiedQuery_user['photos'];
	emails: CompanyHubTopNavigationQuery_user['emails'];
	environment: SessionDataType['environment'];
};

/**
 * The top navigation of the Company Hub app-like experience at Team '25 US.
 *
 * Inspired by `TopNavigation` at confluence/next/packages/app-navigation/src/TopNavigation.tsx
 *
 * @see {@link https://www.figma.com/design/MBdUXhdmgbsIkMkOhQGu5e/Hub-App?node-id=101-52260}
 * @see {@link https://hello.atlassian.net/wiki/spaces/CLI/pages/4856024337/Confluence-less+Team+25+US+Experience}
 */
function CompanyHubTopNavigationComponent() {
	const { displayName, isLoggedIn, environment } = useSessionData();
	const renderServerPlaceholder = useRenderServerPlaceholder();
	const { data: emailData, error: emailError } = useQueryInterop<CompanyHubTopNavigationQuery>(
		graphql`
			query CompanyHubTopNavigationQuery {
				user: confluence_atlassianUser(current: true) {
					id
					emails {
						value
						isPrimary
					}
				}
			}
		`,
		{
			// Skip if we are in SSR mode, we only want to call this query in SPA
			skip: renderServerPlaceholder,
		},
	);
	const isAtlassianStaff = (user: User) => {
		return (
			user?.emails?.some(
				(email) =>
					email?.value &&
					(email.value.endsWith('@atlassian.com') ||
						email.value.endsWith('@atlassian.com.invalid') ||
						email.value.endsWith('@usersinbuckets.com')),
			) || false
		);
	};
	// ATTENTION: Reusing an existing query here is not best practice (do not replicate)! This decision was justified
	// because Company Hub will eventually leave Confluence and we want to reduce any potential issues with SSR until then.
	const { data: photoData, error: photoError } =
		useQueryInterop<AppNavigationUnifiedQueryType>(AppNavigationUnifiedQuery);

	const user: User = {
		id: emailData?.user?.id ?? null,
		displayName,
		photos: photoData?.user?.photos ?? null,
		emails: emailData?.user?.emails ?? null,
		environment,
	};

	const renderError = (error: ApolloErrorInterop) => {
		if (isUnauthorizedError(error) || isNotFoundError(error)) {
			markErrorAsHandled(error);
			return null;
		}
		return <ErrorDisplay error={error} />;
	};

	return (
		<>
			{/* There's no SideNavToggleButton here because:
			 * - there's no side nav in view or edit mode;
			 * - there's side nav in settings screens, but there'd be shift to the right if SideNavToggleButton were rendered.
			 */}
			<TopNavStart sideNavToggleButton={null}>
				<AppSwitcherButton />
				<DeveloperButtons isAtlassianStaff={isAtlassianStaff(user)} />
				<CompanyHubSiteLogoButton />
			</TopNavStart>
			<TopNavMiddle>
				<SearchBar />
			</TopNavMiddle>
			<TopNavEnd>
				<RovoButton />
				<NotificationsButton />
				{isLoggedIn ? <CompanyHubProfileMenu user={user} /> : <LoginButton />}
			</TopNavEnd>
			{emailError && renderError(emailError)}
			{photoError && renderError(photoError)}
		</>
	);
}

export const CompanyHubTopNavigation: React.FC = withErrorBoundary({
	attribution: Attribution.MIGRATIONS_TOOLING,
	attributes: {
		// Inspired by `TopNavigationBase` at confluence/next/packages/app-navigation/entry-points/topNavigation.tsx
		errorBoundaryId: 'company-hub/top-navigation',
	},
})(CompanyHubTopNavigationComponent);
